import API_URLS from '@Constants/apiUrls.constants';
import CommonConstants from '@Constants/common.constants';
import { BuyPhoneNumberResponse } from '@Models/callerIds.models';
import { GetBleedOutStateResponse } from '@Models/report.model';
import {
  ChangeVoiceProviderSettingRequest,
  EditTimeFrameRequest,
  FeatureFlagData,
  GetVoiceProviderSettingOptionsResponse,
  GetVoiceProviderSettingResponse,
  StateTsrData,
  StateTsrQuery,
  StateTsrResponse,
  ToggleFeatureFlagStateRequest,
  StateTsrRequest,
  EditCallMonitorPercentageRequest,
  DialerSubAccountResponse,
  ChangeDialerSubAccountRequest,
  AccountSettingsResponse,
  AccountSettingsRequest,
  GlobalSettingsResponse,
  BuyDialInNumberRequest,
  UpdateGlobalSettingRequest,
} from '@Models/settings.models';
import query from '@Utils/common/query.utils';

import { baseApi } from './baseApi.service';

export const settingsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      'CallRecording',
      'VoiceProvider',
      'AdminSettings',
      'BleedOut',
      'StateTsr',
      'StateTsrModal',
      'DialerSubAccount',
      'AccountSettings',
      'GlobalSettings',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Get call recording setting state by user id
      getCallRecordingState: builder.query<boolean, string>({
        query: (id) =>
          `${API_URLS.SETTINGS.USER_SETTINGS_CALL_RECORDING}/${id}`,
        providesTags: ['CallRecording'],
      }),
      // Change call recording setting state by user id
      changeCallRecordingState: builder.mutation<void, string>({
        query: (id) => ({
          url: `${API_URLS.SETTINGS.USER_SETTINGS_CALL_RECORDING}/${id}`,
          method: 'POST',
        }),
        invalidatesTags: ['CallRecording'],
      }),
      // Get voice provider options settings by user id
      getVoiceProviderOptionsSetting: builder.query<
        GetVoiceProviderSettingOptionsResponse,
        string
      >({
        query: (id) =>
          `${API_URLS.SETTINGS.USER_SETTINGS_VOICE_PROVIDER_OPTIONS}/${id}`,
        providesTags: ['VoiceProvider'],
      }),
      // Get voice provider settings by user id
      getVoiceProviderSetting: builder.query<
        GetVoiceProviderSettingResponse,
        string
      >({
        query: (id) =>
          `${API_URLS.SETTINGS.USER_SETTINGS_VOICE_PROVIDER}/${id}`,
        providesTags: ['VoiceProvider'],
      }),
      // Change voice provider settings by user id
      changeVoiceProviderSetting: builder.mutation<
        void,
        ChangeVoiceProviderSettingRequest
      >({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.USER_SETTINGS_VOICE_PROVIDER}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['VoiceProvider'],
      }),
      // Gets Bleed Out mode state
      getBleedOutState: builder.query<GetBleedOutStateResponse, void>({
        query: () => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_BLEED_OUT}`,
          method: 'GET',
        }),
        providesTags: ['BleedOut'],
      }),
      // Get feature flags list by admin
      getFeatureFlags: builder.query<FeatureFlagData[], void>({
        query: () => `${API_URLS.SETTINGS.ADMIN_SETTINGS}`,
        providesTags: ['AdminSettings'],
      }),
      // Toggle feature flag by admin
      toggleFeatureFlagStateById: builder.mutation<
        void,
        ToggleFeatureFlagStateRequest
      >({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['AdminSettings'],
      }),
      // Edit time frame feature values
      editTimeFrameById: builder.mutation<void, EditTimeFrameRequest>({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_TIME_FRAME}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['AdminSettings'],
      }),
      // Get state TSR list by admin
      getStateTsrList: builder.query<StateTsrResponse, StateTsrQuery>({
        query: ({ direction, ...resStateTsrQuery }) =>
          `${API_URLS.SETTINGS.ADMIN_SETTINGS_STATE_TSR}?${query.stringify({
            ...resStateTsrQuery,
          })}&${direction || CommonConstants.INITIAL_SORT_DIRECTION}`,
        providesTags: ['StateTsr'],
      }),
      // Create state TSR by admin
      addStateTsr: builder.mutation<void, StateTsrRequest>({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_STATE_TSR}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['StateTsr'],
      }),
      // Edit state TSR by admin by id
      editStateTsr: builder.mutation<void, StateTsrData>({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_STATE_TSR}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['StateTsr', 'StateTsrModal'],
      }),
      // Delete state TSR by id
      deleteStateTsr: builder.mutation<void, string>({
        query: (id) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_STATE_TSR}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['StateTsr'],
      }),
      // Get state TSR by id
      getStateTsr: builder.query<StateTsrData, string>({
        query: (id) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_STATE_TSR}/${id}`,
          method: 'GET',
        }),
        providesTags: ['StateTsrModal'],
      }),
      // Edit call monitor percentage feature values
      editCallMonitorPercentageById: builder.mutation<
        void,
        EditCallMonitorPercentageRequest
      >({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ADMIN_SETTINGS_CALL_MONITOR}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['AdminSettings'],
      }),
      // Get subAccount list and subAccount by user id
      getDialerSubAccountOptions: builder.query<
        DialerSubAccountResponse,
        string
      >({
        query: (id) =>
          `${API_URLS.SETTINGS.USER_SETTINGS_DIALER_SUB_ACCOUNT}/${id}`,
        providesTags: ['DialerSubAccount'],
      }),
      // Change subAccount by user id
      changeDialerSubAccount: builder.mutation<
        void,
        ChangeDialerSubAccountRequest
      >({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.USER_SETTINGS_DIALER_SUB_ACCOUNT}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['DialerSubAccount'],
      }),
      // Get account settings by accountId
      getAccountSettings: builder.query<AccountSettingsResponse, string>({
        query: (id) => `${API_URLS.SETTINGS.ACCOUNT_SETTINGS}/${id}`,
        providesTags: ['AccountSettings'],
      }),
      editAccountSettings: builder.mutation<void, AccountSettingsRequest>({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.ACCOUNT_SETTINGS}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['AccountSettings'],
      }),
      getGlobalSettings: builder.query<GlobalSettingsResponse[], void>({
        query: () => `${API_URLS.SETTINGS.GLOBAL_SETTINGS}`,
        providesTags: ['GlobalSettings'],
      }),
      updateGlobalSettings: builder.mutation<void, UpdateGlobalSettingRequest>({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.GLOBAL_SETTINGS}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['GlobalSettings'],
      }),
      // buy dial-in phone number
      buyDialInNumber: builder.mutation<
        BuyPhoneNumberResponse,
        BuyDialInNumberRequest
      >({
        query: (body) => ({
          url: `${API_URLS.SETTINGS.BUY_DIAL_IN_NUMBER}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['GlobalSettings'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetCallRecordingStateQuery,
  useChangeCallRecordingStateMutation,
  useGetVoiceProviderOptionsSettingQuery,
  useChangeVoiceProviderSettingMutation,
  useGetVoiceProviderSettingQuery,
  useGetBleedOutStateQuery,
  useGetFeatureFlagsQuery,
  useToggleFeatureFlagStateByIdMutation,
  useEditTimeFrameByIdMutation,
  useGetStateTsrListQuery,
  useAddStateTsrMutation,
  useEditStateTsrMutation,
  useDeleteStateTsrMutation,
  useGetStateTsrQuery,
  useEditCallMonitorPercentageByIdMutation,
  useGetDialerSubAccountOptionsQuery,
  useChangeDialerSubAccountMutation,
  useGetAccountSettingsQuery,
  useEditAccountSettingsMutation,
  useGetGlobalSettingsQuery,
  useUpdateGlobalSettingsMutation,
  useBuyDialInNumberMutation,
} = settingsApi;
